import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import AiFeaturePreview from "./AiFeaturePreview";
import AiFeatureSourceCards from "./AiFeatureSourceCards";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { TOOL_PREP_FOOTER_HINT, TOOL_PREP_RESULT_FOOTER_HINT, TOOL_PREP_PREPARING_PREVIEW_LABEL } from "../../../../../utils/editorEntrySession";
import { useToolPrepImageDownload } from "../../../hooks/useToolPrepImageDownload";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { resolveCreationImageUrl } from "../../../../../utils/resolveCreationImageUrl";
import { resolveImageSource } from "../../../../../utils/resolveImageSource";
import { useEditorStore } from "../../../../../state/editorStore";
import {
  buildToolPrepHowToUse,
  getToolPrepHowToUse,
} from "../../../../../config/tools/toolPrepHowToUseCatalog";
import {
  aiPrepStageIsTransparent,
  resolveAiPrepStageImage,
} from "./aiPrepStage";
import ToolPrepCreditsBadge from "./ToolPrepCreditsBadge";
import "./AiFeatureToolPrep.css";

/**
 * Shared AI Tools prep shell.
 * aka/bio (+ optional howToSteps/howToTip) feed ToolPrepPopup's How to use modal.
 * Remove Background (when used) sits on the source cards via removeBgControls.
 */
const AiFeatureToolPrep = ({
  toolKey = "",
  title = "",
  aka = "",
  bio = "",
  /** Optional numbered steps for the How to use modal. */
  howToSteps = null,
  /** Optional tip line for the How to use modal. */
  howToTip = "",
  emptyPreviewLabel = "Upload an image to preview",
  loadingLabel = "Working…",
  comingSoonLabel = null,
  emptyFooterHint = "Upload an image, adjust settings, then generate.",
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  /** When set, shows Remove Background on the source cards. */
  removeBgBio = null,
  /**
   * Whether Open in Editor should treat the result as a cutout.
   * null = follow the Remove BG toggle. false = opaque composite (AI Background).
   */
  resultIsCutout = null,
  /** (ctx) => ReactNode — ctx: { busy, clearResult, leftPanelRef, setError, sourceImageUrl } */
  renderControls = null,
  /** (ctx) => ReactNode — replace default AiFeatureSourceCards */
  renderSource = null,
  /** (ctx) => ReactNode — custom right preview; falls back to AiFeaturePreview */
  renderPreview = null,
  /** Called when the user replaces/clears the source image. */
  onSourceChange = null,
  /** Initial original/preview card selection. */
  defaultStageKey = "original",
  /** Filename used when converting blob/data URLs to a File for Generate. */
  generateFileName = null,
  /** () => boolean — extra gate for Generate (AND with source loaded) */
  isGenerateReady = null,
  /** When false, hides footer Generate (e.g. tool owns its own action buttons). */
  showPrimaryAction = true,
  /** When false, hides Open in Editor after a result (e.g. passport download-only). */
  showOpenInEditor = true,
  /** When false, hides footer Download (e.g. download moves to a later page). */
  showDownload = true,
  /** Label for footer Download when shown. */
  downloadLabel = "Download",
  /** Footer hint when a result is ready; defaults to shared TOOL_PREP_RESULT_FOOTER_HINT. */
  resultFooterHint = null,
  /** Download filename fallback when source name is missing. */
  downloadFallbackName = "result.png",
  /** Override primary footer button (e.g. Apply selected on result stage). */
  primaryActionLabel = null,
  /** Optional node rendered beside the Generate / Regenerate label. */
  doneLabelAddon = null,
  /** When false, hides the shared credit badge (passport shows cost on its cards). */
  showCreditBadge = true,
  /** Overrides the badge count (upscale changes with scale, Balanced, and Best). */
  creditCost = null,
  onPrimaryAction = null,
  primaryActionDisabled = false,
  /** Optional secondary footer button (e.g. Skip). */
  secondaryActionLabel = null,
  onSecondaryAction = null,
  secondaryActionDisabled = false,
  secondaryActionVariant = "button",
  /** When true, primary footer action renders before secondary. */
  footerPrimaryFirst = false,
  /** Explicit footer button order (cancel | download | secondary | primary | openInEditor). */
  footerActionOrder = null,
  /** "row" (default) or "stacked" — full-width primary with secondary below. */
  footerActionsLayout = "row",
  /** Extra busy flag from parent tool (e.g. applying add-ons). */
  extraBusy = false,
  /**
   * Target output aspect (w/h). Preview card keeps the upload frame;
   * passport (etc.) result is fitted/centered inside it.
   */
  previewAspectRatio = null,
  /**
   * Optional BFF generate. Receives resolved { token, file, image_url } plus the
   * original source fields, and should return { output_url | shadow_url, creation_id }.
   * When omitted, Generate stays coming-soon.
   */
  onGenerate = null,
  /**
   * When true, Preview stage uses the checkerboard (e.g. transparent BG-remove cutout).
   * Function form receives { hasResult, resultImageUrl }.
   */
  previewTransparent = false,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const [stageKey, setStageKey] = useState(defaultStageKey || "original");
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const resultImageUrlRef = useRef(null);
  const [creationId, setCreationId] = useState(null);
  const [error, setError] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const showRemoveBg = typeof removeBgBio === "string";
  const [removeBg, setRemoveBg] = useState(
    showRemoveBg ? Boolean(removeBgDefault) : false
  );
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [viewingCutout, setViewingCutout] = useState(false);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const bgRequestRef = useRef(null);
  const runBgRemovalRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const leftPanelRef = useRef(null);

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  resultImageUrlRef.current = resultImageUrl;

  const clearResult = useCallback(() => {
    setResultImageUrl(null);
    setCreationId(null);
  }, []);

  const setResult = useCallback((url, cid = null) => {
    setResultImageUrl(url || null);
    setCreationId(cid);
    setViewingCutout(false);
    if (url) setStageKey("preview");
  }, []);

  const runLocalGenerate = useCallback(async (runner) => {
    if (typeof runner !== "function") return null;
    setGenLoading(true);
    setError(null);
    try {
      return await runner();
    } catch (err) {
      setError(err?.message || "Generation failed.");
      throw err;
    } finally {
      setGenLoading(false);
    }
  }, []);

  const handleSourceChange = useCallback(() => {
    clearResult();
    setStageKey("original");
    setError(null);
    setCutoutUrl(null);
    setViewingCutout(false);
    setRemoveBg(showRemoveBg ? Boolean(removeBgDefault) : false);
    setBgError(null);
    if (typeof onSourceChange === "function") onSourceChange();
  }, [clearResult, onSourceChange, removeBgDefault, showRemoveBg]);

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: genLoading || bgLoading,
    onSourceChange: handleSourceChange,
  });

  const runBackgroundRemoval = useCallback(async () => {
    if (
      cutoutUrl &&
      (cutoutUrl.startsWith("blob:") ||
        cutoutUrl.startsWith("data:") ||
        /^https?:\/\//i.test(cutoutUrl))
    ) {
      setStageKey("preview");
      return { cutoutUrl, creationId };
    }
    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);
    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null && !resultImageUrlRef.current) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      setStageKey("preview");
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) setBgLoading(false);
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  runBgRemovalRef.current = runBackgroundRemoval;

  const handleRemoveBgToggle = useCallback(async () => {
    if (!sourceImageUrl || bgLoading || genLoading) return;
    const next = !removeBg;
    setRemoveBg(next);
    setBgError(null);
    if (!next) {
      setViewingCutout(false);
      setStageKey(resultImageUrl ? "preview" : "original");
      return;
    }
    setViewingCutout(true);
    setStageKey("preview");
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
      setViewingCutout(false);
    }
  }, [
    sourceImageUrl,
    bgLoading,
    genLoading,
    removeBg,
    resultImageUrl,
    runBackgroundRemoval,
  ]);

  // Ensure cutout once when Remove BG is on — do not re-fire on loading/callback identity.
  useEffect(() => {
    if (!showRemoveBg || !removeBg || !sourceImageUrl) return;
    if (cutoutUrl) return;
    let cancelled = false;
    (async () => {
      try {
        await runBgRemovalRef.current?.();
      } catch {
        if (!cancelled) setRemoveBg(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRemoveBg, removeBg, sourceImageUrl, cutoutUrl]);

  const busy = genLoading || bgLoading;

  const removeBgControls = useMemo(
    () =>
      showRemoveBg
        ? {
            active: removeBg,
            onToggle: handleRemoveBgToggle,
            disabled: bgLoading || genLoading || !sourceImageUrl,
            bio: removeBgBio || "",
            error: bgError,
          }
        : null,
    [
      showRemoveBg,
      removeBg,
      handleRemoveBgToggle,
      bgLoading,
      genLoading,
      sourceImageUrl,
      removeBgBio,
      bgError,
    ]
  );

  const { downloadBusy, onDownload, downloadFlow } = useToolPrepImageDownload(
    resultImageUrl,
    sourceFileName,
    {
      enabled: showDownload !== false && Boolean(resultImageUrl),
      fallbackName: downloadFallbackName,
      onError: setError,
      variant: "ai",
      toolKey,
      shareTitle: title || "LazyStudio",
      shareText: `I used LazyStudio's ${title || "AI"} tool!`,
    }
  );

  const canGenerate =
    Boolean(sourceImageUrl) &&
    !busy &&
    (typeof isGenerateReady !== "function" || Boolean(isGenerateReady()));

  const previewCardUrl =
    resultImageUrl || (showRemoveBg && removeBg ? cutoutUrl : null);
  const cutoutPending = Boolean(
    showRemoveBg && removeBg && !resultImageUrl && bgLoading
  );

  const stageImageUrl = resolveAiPrepStageImage({
    stageKey,
    sourceImageUrl,
    resultImageUrl,
    removeBg: showRemoveBg && removeBg,
    cutoutUrl,
    viewingCutout,
  });
  const wantsResultTransparent =
    typeof previewTransparent === "function"
      ? Boolean(
          previewTransparent({
            hasResult: Boolean(resultImageUrl),
            resultImageUrl,
          })
        )
      : Boolean(previewTransparent);
  const stageTransparent =
    aiPrepStageIsTransparent({
      stageKey,
      removeBg: showRemoveBg && removeBg,
      cutoutUrl,
      viewingCutout,
      resultImageUrl,
    }) ||
    Boolean(stageKey === "preview" && wantsResultTransparent && resultImageUrl);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    if (typeof onGenerate !== "function") {
      setGenLoading(true);
      setError(null);
      try {
        await new Promise((r) => setTimeout(r, 400));
        setError(
          `${comingSoonLabel || title} generation will be available soon.`
        );
      } finally {
        setGenLoading(false);
      }
      return;
    }

    setGenLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sign in required.");
      const useCutout = Boolean(removeBg && cutoutUrl);
      const onEditor =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/editor");
      const editorIds = onEditor ? useEditorStore.getState() : null;
      const { file, image_url } = await resolveImageSource({
        sourceUrl: useCutout ? cutoutUrl : sourceImageUrl,
        resolvedFile: useCutout ? null : resolvedFile,
        filename: generateFileName || `${toolKey || "ai"}-input.png`,
        token,
        creationId: editorIds?.creationId ?? null,
        rootCreationId: editorIds?.rootCreationId ?? null,
      });
      const data = await onGenerate({
        token,
        file,
        image_url,
        sourceImageUrl,
        resolvedFile,
        removeBg,
        cutoutUrl,
        resultImageUrl: resultImageUrlRef.current,
        stageKey,
      });
      const outputUrl = data?.output_url || data?.shadow_url;
      if (!outputUrl) {
        throw new Error(data?.message || data?.detail || "Generation failed.");
      }
      const { displayUrl, creationId: cid } = await resolveCreationImageUrl(
        token,
        outputUrl,
        data.creation_id
      );
      setResult(displayUrl, cid ?? data.creation_id ?? null);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Generation failed. Please try again.";
      setError(typeof msg === "string" ? msg : "Generation failed.");
    } finally {
      setGenLoading(false);
    }
  };

  const handleClose = () => {
    if (busy || downloadBusy) return;
    onCancel();
  };

  const handleOpenInEditor = async () => {
    if (!resultImageUrl) return;
    try {
      await onDone({
        imageUrl: resultImageUrl,
        creationId,
        // BG Remover result is always a cutout; optional Remove BG tools pass flag when on
        removeBg:
          toolKey === "ai_background_remover" ||
          Boolean(resultIsCutout == null ? showRemoveBg && removeBg : resultIsCutout),
        selectSubject: toolKey === "ai_background_remover",
      });
    } catch (err) {
      setError(err?.message || "Could not open result in editor.");
    }
  };

  const controlCtx = {
    busy,
    clearResult,
    setResult,
    runLocalGenerate,
    leftPanelRef,
    setError,
    sourceImageUrl,
    cutoutUrl,
    bgLoading,
    removeBg,
    resolvedFile,
    resultImageUrl,
    hasResult: Boolean(resultImageUrl),
    onDownload,
    downloadBusy,
    stageKey,
    setStageKey,
  };

  const previewCtx = {
    imageUrl: stageImageUrl,
    resultImageUrl,
    loading: genLoading || (bgLoading && stageKey === "preview"),
    loadingLabel: bgLoading ? "Removing background…" : loadingLabel,
    emptyLabel:
      uploadProgress != null && !stageImageUrl
        ? TOOL_PREP_PREPARING_PREVIEW_LABEL
        : emptyPreviewLabel,
    transparent: stageTransparent,
    hasResult: Boolean(resultImageUrl),
    sourceImageUrl,
    cutoutUrl,
    removeBg: Boolean(showRemoveBg && removeBg),
    showSubjectBox: Boolean(
      stageImageUrl &&
        !genLoading &&
        !(bgLoading && stageKey === "preview") &&
        (toolKey === "ai_background_remover" ||
          (showRemoveBg &&
            removeBg &&
            cutoutUrl &&
            stageImageUrl === cutoutUrl))
    ),
    stageKey,
    uploadProgress,
  };

  const sourceCtx = {
    selectedKey: stageKey,
    onSelect: setStageKey,
    originalImageUrl: sourceImageUrl,
    previewImageUrl: previewCardUrl,
    previewAspectRatio,
    removeBgActive: Boolean(
      showRemoveBg && removeBg && (viewingCutout || !resultImageUrl)
    ),
    cutoutPending,
    onUploadClick: openUploadPicker,
    onRemove: clearSource,
    uploadProgress,
    sourceName: sourceFileName,
    sourceSizeLabel: sourceFileSizeLabel,
    actionsDisabled: busy,
    hasResult: Boolean(resultImageUrl),
    sourceImageUrl,
    resultImageUrl,
    stageKey,
    setStageKey,
    busy,
  };

  const leftContent = (
    <div className="aftp-panel" ref={leftPanelRef}>
      <div className="aftp-scroll tool-prep-scroll" ref={scrollBodyRef}>
        <div className="aftp-source">
          {typeof renderSource === "function" ? (
            renderSource(sourceCtx)
          ) : (
            <AiFeatureSourceCards
              selectedKey={stageKey}
              onSelect={setStageKey}
              originalImageUrl={sourceImageUrl}
              previewImageUrl={previewCardUrl}
              previewAspectRatio={previewAspectRatio}
              removeBgActive={Boolean(
                showRemoveBg && removeBg && (viewingCutout || !resultImageUrl)
              )}
              cutoutPending={cutoutPending}
              onUploadClick={openUploadPicker}
              onRemove={clearSource}
              uploadProgress={uploadProgress}
              sourceName={sourceFileName}
              sourceSizeLabel={sourceFileSizeLabel}
              actionsDisabled={busy}
              hasResult={Boolean(resultImageUrl)}
              removeBgControls={removeBgControls}
            />
          )}
        </div>

        {typeof renderControls === "function"
          ? renderControls(controlCtx)
          : null}

        {error ? <p className="aftp-error">{error}</p> : null}
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [sourceImageUrl, stageKey, toolKey]);

  const howToUse = useMemo(() => {
    const catalog = getToolPrepHowToUse(toolKey);
    return buildToolPrepHowToUse({
      aka: aka || catalog?.aka || "",
      bio: bio || catalog?.summary || "",
      steps: howToSteps,
      tip: howToTip || catalog?.tip || "",
      defaultSteps:
        catalog?.steps?.length > 0
          ? catalog.steps
          : [
              "Upload an image",
              "Adjust the settings on the left",
              "Tap Generate when ready",
            ],
    });
  }, [aka, bio, howToSteps, howToTip, toolKey]);

  const hasCustomPrimary = typeof onPrimaryAction === "function";
  const footerPrimaryClick = hasCustomPrimary ? onPrimaryAction : handleGenerate;
  const footerPrimaryLabel = hasCustomPrimary
    ? primaryActionLabel || "Done"
    : resultImageUrl
      ? "Regenerate"
      : "Generate";
  const footerPrimaryDisabled = hasCustomPrimary
    ? Boolean(primaryActionDisabled)
    : !canGenerate;
  const footerShowDone =
    hasCustomPrimary || showPrimaryAction !== false;

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey={toolKey}
        title={title}
        howToUse={howToUse}
        left={leftContent}
        preview={
          typeof renderPreview === "function" ? (
            renderPreview(previewCtx)
          ) : (
            <AiFeaturePreview
              imageUrl={stageImageUrl}
              loading={previewCtx.loading}
              loadingLabel={previewCtx.loadingLabel}
              emptyLabel={emptyPreviewLabel}
              transparent={stageTransparent}
              showSubjectBox={previewCtx.showSubjectBox}
            />
          )
        }
        onDone={footerPrimaryClick}
        onCancel={handleClose}
        confirmClose={Boolean(sourceImageUrl || resultImageUrl)}
        onDownload={onDownload}
        downloadLabel={downloadLabel}
        onSecondaryAction={
          typeof onSecondaryAction === "function" ? onSecondaryAction : null
        }
        secondaryActionLabel={secondaryActionLabel || "Skip"}
        secondaryActionDisabled={Boolean(secondaryActionDisabled)}
        secondaryActionVariant={secondaryActionVariant}
        footerPrimaryFirst={Boolean(footerPrimaryFirst)}
        footerActionOrder={footerActionOrder}
        footerActionsLayout={footerActionsLayout}
        onOpenInEditor={
          showOpenInEditor !== false && resultImageUrl
            ? handleOpenInEditor
            : undefined
        }
        busy={busy || downloadBusy || Boolean(extraBusy)}
        showDone={footerShowDone}
        doneDisabled={footerPrimaryDisabled || Boolean(extraBusy)}
        doneLabel={
          extraBusy && hasCustomPrimary ? (
            "Applying…"
          ) : (
            <>
              {footerPrimaryLabel}
              {doneLabelAddon}
              {showCreditBadge !== false && !doneLabelAddon ? (
                <ToolPrepCreditsBadge toolKey={toolKey} creditCost={creditCost} />
              ) : null}
            </>
          )
        }
        cancelLabel="Close"
        footerHint={
          error
            ? null
            : resultImageUrl
              ? resultFooterHint || TOOL_PREP_RESULT_FOOTER_HINT
              : sourceImageUrl
                ? TOOL_PREP_FOOTER_HINT
                : emptyFooterHint
        }
        footerHintClassName={undefined}
        bodyOwnsScroll
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey={toolKey}
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
          setResultImageUrl(null);
          setCreationId(null);
          setError(null);
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default AiFeatureToolPrep;
